import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import {
  ISofHeaderEvent,
  ISofMenuHeaderItem,
  ISofMenuItem,
  ISofNotificationsConfig,
  SofAppShellComponent,
  SofFloatingModalService,
  SofHeaderService,
  SofMenuService,
  SofNotificationsFloatingModalComponent,
  SofNotificationsFloatingModalService,
  SofToastService,
  SofUserFloatingModalComponent,
  SofUserMenuService,
} from '@softion/web-components';
import { Subject, filter, takeUntil } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from '../shared/services/auth.service';
import { DesignerLinkService } from '../shared/services/designer-link.service';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [RouterModule, SofAppShellComponent],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
})
export class LayoutComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly sofMenuService = inject(SofMenuService);
  private readonly sofHeaderService = inject(SofHeaderService);
  private readonly sofToastService = inject(SofToastService);
  private readonly sofFloatingModalService = inject(SofFloatingModalService);
  private readonly sofNotificationsFloatingModal = inject(SofNotificationsFloatingModalService);
  private readonly sofUserMenuService = inject(SofUserMenuService);
  private readonly auth      = inject(AuthService);
  private readonly designer  = inject(DesignerLinkService);
  private readonly destroy$ = new Subject<void>();

  readonly logoFull = environment.logoFull;
  readonly logoSmall = environment.logoSmall;

  readonly idFloatingUser = 'floating-modal-user';

  private _isDark = false;

  private readonly menu: ISofMenuItem[] = this._buildMenu();

  readonly menuHeader: ISofMenuHeaderItem[] = [
    {
      id: 'notifications',
      icon: 'fa fa-bell sof-color-info',
      tooltip: 'Notificaciones',
      action: 'floatingModal',
      badge: 0,
      disabled: false,
      show: true,
    },
    {
      id: 'theme',
      icon: 'fa fa-moon',
      tooltip: 'Tema',
      action: 'function',
      show: true,
    },
  ];

  readonly configNotifications: ISofNotificationsConfig = {
    showMarkAll: true,
    showClearAll: true,
    showPerItemMarkRead: true,
    showPerItemDelete: true,
    compactTime: true,
    maxHeight: 420,
  };

  ngOnInit(): void {
    this.initMenu();
    this.initHeader();
    this.initNotifications();
    this.initProfile();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initMenu(): void {
    this.sofMenuService.setDense(true);
    this.sofMenuService.setMenu(this.menu);
    this.syncMenuSelection(this.router.url);

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => this.syncMenuSelection(event.urlAfterRedirects));

    this.sofMenuService.itemClick$
      .pipe(takeUntil(this.destroy$))
      .subscribe((item) => this.handleSelectMenu(item));
  }

  private initHeader(): void {
    const user = this.auth.user();
    this.sofHeaderService.setConfig({
      avatar: {
        show: true,
        fullName: user?.sName ?? 'Admin',
      },
    });

    const saved = localStorage.getItem('sof-color-scheme');
    if (saved === 'dark' || saved === 'light') {
      this._isDark = saved === 'dark';
      document.documentElement.dataset['colorScheme'] = saved;
    } else {
      this._isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    const themeBtn = this.menuHeader.find((m) => m.id === 'theme');
    if (themeBtn) {
      themeBtn.icon    = this._isDark ? 'fa fa-sun'  : 'fa fa-moon';
      themeBtn.tooltip = this._isDark ? 'Modo claro' : 'Modo oscuro';
    }

    this.sofHeaderService.setMenu(this.menuHeader);
    this.sofHeaderService.events$.subscribe((item) => this.handleSelectMenuHeader(item));
    this.sofHeaderService.avatar$.subscribe((item) => {
      if (item?.originalEvent) this.openFloatingUser(item.originalEvent);
    });
  }

  private initNotifications(): void {
    this.sofNotificationsFloatingModal.events$
      .pipe(takeUntil(this.destroy$))
      .subscribe((evt) => {
        switch (evt.type) {
          case 'markAllRequested':
            this.sofNotificationsFloatingModal.markAllRead();
            break;
          case 'clearAllRequested':
            this.sofNotificationsFloatingModal.clearAll();
            break;
          case 'markOneRequested':
            this.sofNotificationsFloatingModal.markOneRead(evt.id);
            break;
          case 'deleteOneRequested':
            this.sofNotificationsFloatingModal.deleteOne(evt.id);
            break;
        }
      });

    this.sofNotificationsFloatingModal.items$
      .pipe(takeUntil(this.destroy$))
      .subscribe((list) => {
        const unread = list.filter((n) => !n.read).length;
        if (unread > 0) this.sofHeaderService.setBadge('notifications', unread);
        else this.sofHeaderService.clearBadge('notifications');
      });
  }

  private initProfile(): void {
    const user = this.auth.user();
    this.sofUserMenuService.setProfile({
      fullName: user?.sName ?? 'Admin',
      email: user?.email ?? '',
      roleLabel: user?.rol ?? '',
    });
    this.sofUserMenuService.setItems([
      { id: 'logout', label: 'Cerrar sesión', icon: 'fa fa-sign-out' },
    ]);
    this.sofUserMenuService.events$.subscribe((item) => {
      if (item?.type === 'itemRequested' && item.item?.id === 'logout') {
        this.handleLogout();
      }
    });
  }

  handleSelectMenu(item: ISofMenuItem): void {
    if (item.action === 'route') {
      this.sofMenuService.select(item.id);
      void this.router.navigateByUrl(item.href ?? '');
    } else if (item.action === 'action' && item.id === 'abrir-webly') {
      this.openDesigner();
    }
  }

  openDesigner(orgId?: string): void {
    this.designer.openDesigner(orgId, this.auth.user()?.storageSignature);
  }

  private syncMenuSelection(rawUrl: string): void {
    const url = rawUrl.split('?')[0].split('#')[0];
    const match = this.menu
      .filter((item) => item.type === 'item' && item.href)
      .find((item) => (item.href === '/' ? url === '/' : url.startsWith(item.href ?? '')));

    if (match) this.sofMenuService.select(match.id);
  }

  handleSelectMenuHeader(item: ISofHeaderEvent): void {
    if (item.item.id === 'notifications' && item.originalEvent) {
      this.openFloatingNotifications(item.originalEvent);
    }
    if (item.item.id === 'theme') {
      this._isDark = !this._isDark;
      document.documentElement.dataset['colorScheme'] = this._isDark ? 'dark' : 'light';
      localStorage.setItem('sof-color-scheme', this._isDark ? 'dark' : 'light');
      const themeBtn = this.menuHeader.find((m) => m.id === 'theme');
      if (themeBtn) {
        themeBtn.icon    = this._isDark ? 'fa fa-sun'  : 'fa fa-moon';
        themeBtn.tooltip = this._isDark ? 'Modo claro' : 'Modo oscuro';
        this.sofHeaderService.setMenu([...this.menuHeader]);
      }
    }
  }

  private openFloatingNotifications(e: Event): void {
    this.sofFloatingModalService.openFloatingModal({
      id: 'modal-notifications',
      component: SofNotificationsFloatingModalComponent,
      data: { config: this.configNotifications },
      position: 'near',
      triangle: true,
      width: 360,
      withBackdrop: false,
      event: e as MouseEvent,
    });
  }

  private openFloatingUser(e: Event): void {
    this.sofFloatingModalService.openFloatingModal({
      id: this.idFloatingUser,
      component: SofUserFloatingModalComponent,
      data: { version: '1.0.0' },
      position: 'near',
      triangle: true,
      width: 300,
      withBackdrop: false,
      event: e as MouseEvent,
    });
  }

  async handleLogout(): Promise<void> {
    this.sofToastService.openToast({
      id: 'alert-close-session',
      title: '¡Éxito!',
      message: 'Sesión cerrada correctamente.',
      position: 'top-right',
    });
    this.sofFloatingModalService.closeModalById(this.idFloatingUser);
    await this.auth.logoutAsync();
    void this.router.navigate(['/login']);
  }

  private _buildMenu(): ISofMenuItem[] {
    return [
      {
        id: 'welcome',
        label: 'Inicio',
        tooltip: 'Inicio',
        iconStart: 'fa fa-house',
        type: 'item',
        action: 'route',
        href: '/',
      },
      { type: 'separator', id: 'sep-cuenta', label: 'Cuenta' },
      {
        id: 'mi-cuenta',
        label: 'Mi cuenta',
        tooltip: 'Mi cuenta',
        type: 'item',
        iconStart: 'fa fa-user-circle',
        action: 'route',
        href: '/mi-cuenta',
      },
      // ── Storage (separador + items) ─────────────────────────────────────
      // Agregar más items de storage aquí conforme se integren los endpoints
      { type: 'separator', id: 'sep-storage', label: 'Almacenamiento' },
      {
        id: 'integraciones',
        label: 'Integraciones',
        tooltip: 'Buckets de Firebase Storage',
        type: 'item',
        iconStart: 'fa fa-plug',
        action: 'route',
        href: '/backoffice/storage/integraciones',
      },
      {
        id: 'system-config',
        label: 'System Config',
        tooltip: 'Configuración global del Storage',
        type: 'item',
        iconStart: 'fa fa-gear',
        action: 'route',
        href: '/backoffice/storage/system-config',
      },
      {
        id: 'organizaciones',
        label: 'Organizaciones',
        tooltip: 'Gestión de organizaciones',
        type: 'item',
        iconStart: 'fa fa-building',
        action: 'route',
        href: '/backoffice/organizaciones',
      },
      {
        id: 'soluciones',
        label: 'Soluciones',
        tooltip: 'Soluciones, planes y complementos',
        type: 'item',
        iconStart: 'fa fa-cubes',
        action: 'route',
        href: '/backoffice/soluciones',
      },
      {
        id: 'ordenes-compra',
        label: 'Órdenes de compra',
        tooltip: 'Alta y cobro de nuevas tiendas',
        type: 'item',
        iconStart: 'fa fa-file-invoice-dollar',
        action: 'route',
        href: '/backoffice/ordenes-compra',
      },
      // ── Acceso rápido al site builder ────────────────────────────────────
      { type: 'separator', id: 'sep-tools', label: 'Herramientas' },
      {
        id: 'abrir-webly',
        type: 'item',
        label: 'Abrir Webly',
        tooltip: 'Abrir el diseñador en una nueva pestaña',
        iconStart: 'fa fa-arrow-up-right-from-square',
        action: 'action',
      },
      // ── WEBLY-WEB — Plantillas ────────────────────────────────────────────
      { type: 'separator', id: 'sep-webpages', label: 'WEBLY-WEB' },
      {
        id: 'global-templates',
        type: 'item',
        label: 'Plantillas',
        tooltip: 'Plantillas WEBLY-WEB administradas por Softion',
        iconStart: 'fa fa-file-code',
        action: 'route',
        href: '/backoffice/webpages/global-templates',
      },
      // ── Catálogo Webly (grupo colapsable) ───────────────────────────────
      { type: 'separator', id: 'sep-catalogo', label: 'Catálogo Webly' },
      {
        id: 'catalogo',
        type: 'item',
        label: 'Catálogo Webly',
        tooltip: 'Variantes de componentes y Design Packs',
        iconStart: 'fa fa-swatchbook',
        expanded: false,
        children: [
          {
            id: 'catalogo-componentes',
            type: 'item',
            label: 'Componentes',
            tooltip: 'Variantes de secciones',
            iconStart: 'fa fa-puzzle-piece',
            action: 'route',
            href: '/backoffice/catalogo/componentes',
          },
          {
            id: 'catalogo-packs',
            type: 'item',
            label: 'Design Packs',
            tooltip: 'Identidades visuales premium',
            iconStart: 'fa fa-layer-group',
            action: 'route',
            href: '/backoffice/catalogo/packs',
          },
        ],
      },
    ];
  }
}
